import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import PostCard from "@/components/blog/PostCard";
import Breadcrumbs from "@/components/blog/Breadcrumbs";
import NewsletterInline from "@/components/blog/NewsletterInline";
import AppDownloadCTA from "@/components/blog/AppDownloadCTA";
import StickyMobileAppBar from "@/components/blog/StickyMobileAppBar";
import { POSTS, CATEGORIES } from "@/content/posts";
import { SITE_URL } from "@/constants/storeLinks";

const BlogIndex = () => {
  const featured = POSTS.find((p) => p.featured) ?? POSTS[0];
  const rest = POSTS.filter((p) => p.slug !== featured?.slug);
  const canonical = `${SITE_URL}/blog`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Blog",
        "@id": `${canonical}#blog`,
        url: canonical,
        name: "PieFolio AI Blog",
        description:
          "Neutral broker comparisons, portfolio strategy, and AI investing for everyday investors in Europe and the UK.",
        publisher: { "@id": `${SITE_URL}/#organization` },
        blogPost: POSTS.map((p) => ({
          "@type": "BlogPosting",
          headline: p.title,
          url: `${SITE_URL}/blog/${p.slug}`,
          datePublished: p.date,
          dateModified: p.lastUpdated,
          author: { "@type": "Organization", name: "PieFolio AI" },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Blog — PieFolio AI: Broker Comparisons & AI Investing</title>
        <meta
          name="description"
          content="Neutral broker comparisons, portfolio strategy, and AI investing for everyday investors in Europe and the UK."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content="PieFolio AI Blog — Broker Comparisons & AI Investing" />
        <meta
          property="og:description"
          content="Neutral broker comparisons, portfolio strategy, and AI investing for everyday investors."
        />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 md:pt-32 pb-20 px-6 md:px-8">
        <div className="container mx-auto max-w-6xl">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Blog" }]} />

          <header className="mt-6 mb-12 max-w-3xl">
            <span className="inline-block text-xs uppercase tracking-widest text-primary font-semibold mb-3">
              PieFolio AI Blog
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground tracking-tight">
              Honest investing, broker comparisons & AI portfolio analysis.
            </h1>
            <p className="mt-5 text-lg text-foreground/70 leading-relaxed">
              Neutral, methodology-driven articles for everyday investors in
              Europe and the UK. No hype, no affiliate-pumping, no financial advice.
            </p>
          </header>

          {/* Categories chip row */}
          <div className="flex flex-wrap gap-2 mb-10">
            {Object.values(CATEGORIES).map((c) => (
              <Link
                key={c.slug}
                to={`/blog/category/${c.slug}`}
                className="text-sm px-4 py-2 rounded-full border border-border hover:border-primary/40 hover:text-primary transition"
              >
                {c.name}
              </Link>
            ))}
          </div>

          {featured && (
            <section className="mb-12">
              <PostCard post={featured} featured />
            </section>
          )}

          <section className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((p) => (
              <PostCard key={p.slug} post={p} />
            ))}
          </section>

          <div className="mt-16">
            <NewsletterInline source="blog-index" />
          </div>

          <div className="mt-12">
            <AppDownloadCTA source="blog-index-footer" />
          </div>
        </div>
      </main>

      <Footer />
      <StickyMobileAppBar />
    </>
  );
};

export default BlogIndex;
